/**
 * PORTFOLIO MILESTONES — the dated points a steering meeting reads across every
 * project. Pure: it takes what the portfolio already loads (a row's schedule, the
 * board's phases, its standalone milestone rows) and derives the milestone set,
 * each with a planned → forecast → actual read and a slip state. The daily
 * forecast snapshots (milestone_pulse, 0124) turn into a slip series for the
 * drill's sparkline.
 *
 * THE HYBRID (0124): three sources, one shape —
 *   • the go-live (every project's headline milestone), planned/forecast/slip
 *     straight off the row's schedule signal;
 *   • a phase end promoted with `is_milestone`;
 *   • a standalone milestone row (a point that is not a phase end).
 */
import type { PortfolioRow } from './portfolio';
import type { Phase } from './phases';
import { phaseShownStatus } from './phases';
import type { Issue } from './types';
import type { Forecast } from './insights';

export type MilestoneKind = 'contract' | 'signoff' | 'golive' | 'delivery' | 'regulatory' | 'other';
export type MilestoneState = 'on-track' | 'slipping' | 'late' | 'done';

/**
 * THE WORK A MILESTONE GATES — the tie that lets the portfolio forecast a checkpoint
 * from the board's cadence (0215, project-milestone-forecast-from-cadence). `phases`
 * is one phase id (one phase), several ids (several phases), or exactly one id with a
 * `narrow` (part of a phase — the cards in that phase under one area or epic). Cards
 * carry their phase id and area/epic, so this resolves to a card set with no per-card
 * tagging. Absent/undefined = no tie: the milestone reads "from the plan" as before.
 */
export interface MilestoneTie {
  /** The phase id(s) whose open cards this checkpoint is waiting on. One, several, or
   *  (with `narrow`) exactly one. */
  phases: string[];
  /** Part of a phase: narrow the one tied phase to a single area or epic within it.
   *  Only meaningful when `phases` has exactly one id. */
  narrow?: { kind: 'area' | 'epic'; id: string };
}

/** The kinds a user may choose, in the order Vinnie set (9 Oct 2026) — the full
 *  set, Contract first, Other last. The Kind select on the add/edit form reads this. */
export const MILESTONE_KINDS: MilestoneKind[] = [
  'contract', 'delivery', 'golive', 'signoff', 'regulatory', 'other',
];

/** What each kind is called on screen — the kind tag, the select, the schedule read. */
export const MILESTONE_KIND_LABELS: Record<MilestoneKind, string> = {
  contract: 'Contract',
  delivery: 'Delivery',
  golive: 'Go-Live',
  signoff: 'Sign-off',
  regulatory: 'Regulatory',
  other: 'Other',
};

/** One derived milestone, ready to draw on the timeline / drill / roll-up. */
export interface Milestone {
  /** Unifies the three sources: 'golive' | 'phase:<id>' | 'ms:<id>' — also the
   *  key the daily snapshot (milestone_pulse) is stored under. */
  key: string;
  name: string;
  kind: MilestoneKind;
  boardId: string;
  projectName: string;
  /** The baseline day (YYYY-MM-DD), the day it was planned to land. */
  plannedDay?: string;
  /** The day it is now forecast to land — the plan when nothing has moved. */
  forecastDay?: string;
  /** The day it actually landed, once it has; absent until then. */
  actualDay?: string;
  state: MilestoneState;
  /** Days the forecast/actual is past the plan (0 when on plan or unknown). */
  slipDays: number;
  /** A standalone milestone the user can edit; a derived marker (go-live, a
   *  promoted phase end) has no row of its own — it reads "from the plan". For a
   *  'ms:<id>' milestone this is the `milestones` row id, so the register and the
   *  schedule of values can read what is billed against it. */
  rowId?: string;
  /** Whether this milestone is a PAYMENT POINT — invoices can claim against it.
   *  Only standalone milestones carry a value (#374 + milestone payments). */
  billable?: boolean;
  /** The amount the stage is worth, when billable — the schedule-of-values figure
   *  invoices claim against. A slice of the contract, NEVER added into spend. */
  billValue?: number;
  /** The work this milestone gates (0215) — the scope the cadence forecast consumes.
   *  Only a standalone milestone carries one; a go-live ties to the whole board and a
   *  promoted phase end ties to its own phase, both implicitly. Absent = no tie. */
  forecastTie?: MilestoneTie;
  /** The cadence forecast for this milestone (attached by the portfolio read via
   *  {@link withMilestoneForecasts}) — present when the milestone has a scope to
   *  predict over (a go-live, a phase end, or a tied checkpoint). `forecast.ready`
   *  says whether there is enough board history to show a confident date rather than
   *  the cold-start fallback. Absent when there is no tie (reads "from the plan"). */
  forecast?: Forecast;
}

/** A standalone milestone as read back from the `milestones` table. */
export interface MilestoneRow {
  id: string;
  name: string;
  kind: MilestoneKind;
  targetDay?: string | null;
  forecastDay?: string | null;
  actualDay?: string | null;
  position?: number;
  /** A payment point — invoices can claim against it (milestone payments). */
  billable?: boolean;
  /** The amount this stage is worth, when billable (`bill_value`, numeric). */
  billValue?: number | null;
  /** The work this checkpoint gates — drives the cadence forecast (0215). Null/absent
   *  = no tie (reads "from the plan"). A jsonb that rides whole through the seam. */
  forecastTie?: MilestoneTie | null;
}

/**
 * WHAT A BILLABLE MILESTONE IS WORTH AND WHAT HAS BEEN CLAIMED AGAINST IT — read
 * straight from the invoice ledger, never typed and never added into spend. The
 * schedule of values (Budget) and the Plan register both read this, so the two
 * surfaces can never disagree. `claimed` counts every live line billed against the
 * milestone at any stage; `paid` only the settled ones; `remaining` is what is left
 * of the stage's value (negative when it has been over-claimed — a soft warning,
 * never a block).
 */
export interface MilestoneBilling {
  scheduled: number;
  claimed: number;
  paid: number;
  remaining: number;
  state: 'paid' | 'claimed' | 'part-claimed' | 'scheduled';
  /** Claimed more than the stage is worth — surfaced as an amber warning only. */
  over: boolean;
}

/** One line of the ledger, as these helpers read it (structural, to stay
 *  decoupled from the full InvoiceLine; `status === 'paid'` is the settled stage). */
interface BillingLine {
  id: string;
  milestone?: string;
  amount: number;
  status: string;
}

export function milestoneBilling(
  milestoneId: string,
  billValue: number,
  lines: ReadonlyArray<BillingLine>,
): MilestoneBilling {
  let claimed = 0;
  let paid = 0;
  for (const l of lines) {
    if (l.milestone !== milestoneId) continue;
    claimed += l.amount;
    if (l.status === 'paid') paid += l.amount;
  }
  const remaining = billValue - claimed;
  const over = claimed > billValue;
  const state: MilestoneBilling['state'] =
    claimed === 0 ? 'scheduled'
      : billValue > 0 && paid >= billValue ? 'paid'
      : billValue > 0 && claimed >= billValue ? 'claimed'
      : 'part-claimed';
  return { scheduled: billValue, claimed, paid, remaining, state, over };
}

/**
 * THE CLAIM A SINGLE INVOICE LINE MAKES against a milestone — for the claim-of-value
 * read in the invoice drawer. `thisClaim` is the line's own amount (claims are free,
 * so a part-claim / progress bill is natural); the others are what OTHER lines have
 * already put against the stage. `leftToClaim` can go negative — the over-claim the
 * drawer shows as a soft amber warning, never a block.
 */
export interface MilestoneClaim {
  scheduled: number;
  paidByOthers: number;
  claimedByOthers: number;
  thisClaim: number;
  leftToClaim: number;
  pct: number;
  over: boolean;
}

export function milestoneClaim(
  milestoneId: string,
  billValue: number,
  lines: ReadonlyArray<BillingLine>,
  thisLineId: string,
  thisClaim: number,
): MilestoneClaim {
  let paidByOthers = 0;
  let claimedByOthers = 0;
  for (const l of lines) {
    if (l.milestone !== milestoneId || l.id === thisLineId) continue;
    claimedByOthers += l.amount;
    if (l.status === 'paid') paidByOthers += l.amount;
  }
  const totalClaimed = claimedByOthers + thisClaim;
  return {
    scheduled: billValue,
    paidByOthers,
    claimedByOthers,
    thisClaim,
    leftToClaim: billValue - totalClaimed,
    pct: billValue > 0 ? (thisClaim / billValue) * 100 : 0,
    over: totalClaimed > billValue,
  };
}

/**
 * THE BILLABLE MILESTONES AGAINST THE CONTRACT — the reconciliation nudge shown on
 * both the Plan register and the schedule of values. A GUIDE, not a limit: the model
 * deliberately drops any hard "sum to contract", so `unallocated` can be positive
 * (room left) or negative (planned beyond the contract) and is only ever an amber
 * note. Undefined contract → no reconciliation to show.
 */
export function milestonesVsContract(
  milestones: ReadonlyArray<{ billable?: boolean; billValue?: number | null }>,
  contractValue: number | undefined | null,
): { billableTotal: number; contractValue: number | null; unallocated: number | null } {
  const billableTotal = milestones.reduce(
    (sum, m) => sum + (m.billable && m.billValue ? m.billValue : 0),
    0,
  );
  const cv = contractValue ?? null;
  return { billableTotal, contractValue: cv, unallocated: cv == null ? null : cv - billableTotal };
}

/** A daily forecast snapshot as read from `milestone_pulse`. */
export interface MilestonePulse {
  milestoneKey: string;
  pulseOn: string;
  forecastDay?: string | null;
  /** The cadence prediction (p50) as it stood that day — 0215. Null on a day the
   *  milestone had no tie or too little history to forecast. Lets the drill plot the
   *  PREDICTION drifting, not just the plan date. */
  predictedDay?: string | null;
  status?: MilestoneState | null;
}

const DAY = 86_400_000;
/** `YYYY-MM-DD` → a whole-day number (UTC), so day gaps are timezone-exact. */
const dayNum = (iso?: string | null): number | undefined =>
  iso ? Math.floor(Date.parse(`${iso}T00:00:00Z`) / DAY) : undefined;
const todayNum = (now: number): number => Math.floor(Date.parse(`${new Date(now).toISOString().slice(0, 10)}T00:00:00Z`) / DAY);
/** A millisecond instant as its plain UTC day. */
export const msToDay = (ms?: number | null): string | undefined =>
  ms != null ? new Date(ms).toISOString().slice(0, 10) : undefined;

/**
 * THE STATE AND SLIP of one milestone from its three dates. Done once it has an
 * actual day (slip measured against the plan); late once the plan is behind us
 * with no actual; slipping when the forecast has drifted past a still-future
 * plan; on track otherwise.
 */
export function milestoneState(
  plannedDay: string | undefined | null,
  forecastDay: string | undefined | null,
  actualDay: string | undefined | null,
  now: number,
): { state: MilestoneState; slipDays: number } {
  const today = todayNum(now);
  const p = dayNum(plannedDay);
  const a = dayNum(actualDay);
  if (a != null) return { state: 'done', slipDays: p != null ? Math.max(0, a - p) : 0 };
  const f = dayNum(forecastDay);
  if (p != null && p < today) return { state: 'late', slipDays: today - p };
  if (p != null && f != null && f > p) return { state: 'slipping', slipDays: f - p };
  return { state: 'on-track', slipDays: 0 };
}

const KIND_OF_PHASE: MilestoneKind = 'delivery';

/**
 * ONE PROJECT'S MILESTONES — the go-live, any promoted phase ends, and the
 * standalone rows, each derived to the same shape. `row` carries the go-live's
 * schedule (planDay/forecastDay); `phases` are the board's own (live + flagged);
 * `standalone` are its `milestones` rows; `cards` are the board's issues, needed to
 * read a promoted phase's CANONICAL done-ness (phaseShownStatus) and its real finish
 * day — see the phase-ends loop below.
 */
export function projectMilestones(
  row: Pick<PortfolioRow, 'id' | 'name' | 'planDay' | 'forecastDay'>,
  phases: Phase[],
  standalone: MilestoneRow[],
  now: number,
  cards: Issue[] = [],
): Milestone[] {
  const out: Milestone[] = [];

  // The day a phase ACTUALLY finished, when the database never stamped `done_at`:
  // the latest completion among its delivered cards. (A phase whose cards were all
  // resolved BEFORE the 0100 trigger shipped reads done everywhere via
  // phaseShownStatus, but its stored status/done_at stayed behind — so the milestone
  // read undefined and showed no actual date. This recovers the real day from the
  // cards, the same source every other "is this phase done" read uses.)
  const latestDoneDay = (cs: Issue[]): string | undefined => {
    let latest: number | undefined;
    for (const c of cs) {
      if (c.status === 'done' && c.completedAt != null && (latest == null || c.completedAt > latest)) latest = c.completedAt;
    }
    return msToDay(latest);
  };
  const phaseFinishDay = (phaseId: string): string | undefined => latestDoneDay(cards.filter((c) => c.phase === phaseId));
  const isResolved = (s: Issue['status']): boolean => s === 'done' || s === 'wontdo';
  const mk = (
    key: string,
    name: string,
    kind: MilestoneKind,
    plannedDay?: string | null,
    forecastDay?: string | null,
    actualDay?: string | null,
  ): Milestone => {
    const { state, slipDays } = milestoneState(plannedDay, forecastDay, actualDay, now);
    return {
      key, name, kind, boardId: row.id, projectName: row.name,
      plannedDay: plannedDay ?? undefined,
      forecastDay: forecastDay ?? undefined,
      actualDay: actualDay ?? undefined,
      state, slipDays,
    };
  };

  // The go-live — the headline milestone, its forecast the row's own projection.
  if (row.planDay) out.push(mk('golive', 'Go-Live', 'golive', row.planDay, row.forecastDay ?? row.planDay));

  // Promoted phase ends — the plan is the target, the actual is the phase's finish.
  // Done-ness is the CANONICAL phaseShownStatus (every card resolved, or a manual
  // override), NOT the stored `p.status` — which can lag the cards (0100 only stamps
  // it going forward), leaving a phase that reads Done everywhere with a milestone
  // that had no actual date. The finish day is the stamped `done_at` when present,
  // else recovered from the cards' own completion.
  for (const p of phases) {
    if (p.retiredAt || !p.isMilestone) continue;
    const done = phaseShownStatus(p, cards) === 'done';
    const actual = done ? (msToDay(p.doneAt) ?? phaseFinishDay(p.id)) : undefined;
    out.push(mk(`phase:${p.id}`, (p.milestoneLabel?.trim() || p.name), KIND_OF_PHASE, p.targetEnd, p.targetEnd, actual));
  }

  // Standalone points — a manual forecast overrides the target; else the target is
  // it. These are the only editable milestones, and the only ones that can be a
  // payment point, so they carry the row id and their billable value.
  //
  // LANDS FROM ITS GATED WORK (Vinnie, 2 Nov 2026): a standalone milestone has no
  // `actual_day` write path, so a tied one whose every gated card was done still read
  // Late/Upcoming forever. Now a milestone that GATES work (a forecast tie) lands the
  // moment all its gated cards are resolved, on the day the last one finished — the
  // same card-derived finish a promoted phase uses. An explicit `actualDay`, if ever
  // stored, still wins; an untied point (no gated work to read) is unchanged.
  for (const m of standalone) {
    let actual = m.actualDay ?? undefined;
    if (!actual && m.forecastTie) {
      const scope = milestoneScope({ key: `ms:${m.id}`, forecastTie: m.forecastTie } as Milestone, cards);
      if (scope && scope.length > 0 && scope.every((c) => isResolved(c.status))) actual = latestDoneDay(scope);
    }
    out.push({
      ...mk(`ms:${m.id}`, m.name, m.kind, m.targetDay, m.forecastDay ?? m.targetDay, actual),
      rowId: m.id,
      billable: !!m.billable,
      billValue: m.billValue ?? undefined,
      forecastTie: m.forecastTie ?? undefined,
    });
  }

  // Soonest first, then by name, so a lane and a "next" read the same order.
  return out.sort((x, y) => (effDay(x) ?? Infinity) - (effDay(y) ?? Infinity) || x.name.localeCompare(y.name));
}

/** The day a milestone effectively lands on — actual, else forecast, else plan. */
export function effDay(m: Milestone): number | undefined {
  return dayNum(m.actualDay) ?? dayNum(m.forecastDay) ?? dayNum(m.plannedDay);
}

/**
 * THE CARDS A MILESTONE'S CADENCE FORECAST CONSUMES (0215) — resolve a derived
 * milestone to the board cards whose completion it is waiting on, or `null` when it
 * has no scope at all (a standalone point with no tie: it reads "from the plan", never
 * a predicted date). The go-live gates the WHOLE board (Vinnie's default — it forecasts
 * out of the box); a promoted phase end gates its own phase (the id rides in the key);
 * a tied standalone gates the open work in its phase(s), optionally narrowed to one
 * area or epic within a single phase. The caller hands in the board's issues; the
 * returned set is every matching card (open and done) — {@link forecastScoped} counts
 * the still-open ones as the scope its run has to consume.
 */
export function milestoneScope(m: Milestone, issues: Issue[]): Issue[] | null {
  if (m.key === 'golive') return issues;
  if (m.key.startsWith('phase:')) {
    const phaseId = m.key.slice('phase:'.length);
    return issues.filter((i) => i.phase === phaseId);
  }
  const tie = m.forecastTie;
  if (!tie || tie.phases.length === 0) return null;
  const phases = new Set(tie.phases);
  let scope = issues.filter((i) => i.phase != null && phases.has(i.phase));
  // A narrow applies only to "part of a phase" — exactly one tied phase.
  if (tie.narrow && tie.phases.length === 1) {
    const { kind, id } = tie.narrow;
    scope = scope.filter((i) => (kind === 'area' ? i.area === id : i.epic === id));
  }
  return scope;
}

/** The ISO-week key (`YYYY-Www`, UTC) a day falls in — so two go-lives in the
 *  same week collide even across a month boundary. */
export function weekKeyOf(day?: string): string | undefined {
  const n = dayNum(day);
  if (n == null) return undefined;
  // Thursday of this ISO week decides the year (ISO-8601). n is days since epoch;
  // 1970-01-01 was a Thursday (weekday 4 with Monday=1), so shift to Monday first.
  const dow = (((n % 7) + 7) % 7 + 3) % 7; // 0 = Monday
  const thursday = n - dow + 3;
  const year = new Date(thursday * DAY).getUTCFullYear();
  const jan1 = Math.floor(Date.parse(`${year}-01-01T00:00:00Z`) / DAY);
  const week = Math.floor((thursday - jan1) / 7) + 1;
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/**
 * GO-LIVE COLLISIONS — two or more projects going live in the same week is the
 * one thing a steering meeting must decide (move one). This groups the open
 * go-lives by their forecast week and returns only the weeks with more than one,
 * each with the milestones that clash — the timeline bands and tags these.
 */
export function goLiveCollisions(milestones: Milestone[]): { weekKey: string; milestones: Milestone[] }[] {
  const byWeek = new Map<string, Milestone[]>();
  for (const m of milestones) {
    if (m.kind !== 'golive' || m.state === 'done') continue;
    const wk = weekKeyOf(m.forecastDay ?? m.plannedDay);
    if (!wk) continue;
    const arr = byWeek.get(wk);
    if (arr) arr.push(m);
    else byWeek.set(wk, [m]);
  }
  return [...byWeek.entries()]
    .filter(([, ms]) => ms.length > 1)
    .map(([weekKey, ms]) => ({ weekKey, milestones: ms }))
    .sort((a, b) => a.weekKey.localeCompare(b.weekKey));
}

/**
 * THE NEXT MILESTONE for the roll-up cell — the most pressing one still open: the
 * open milestone with the earliest effective day (so an overdue one surfaces
 * ahead of an upcoming one). Undefined when a project has none open.
 */
export function nextMilestone(milestones: Milestone[], _now: number): Milestone | undefined {
  const open = milestones.filter((m) => m.state !== 'done' && effDay(m) != null);
  if (open.length === 0) return undefined;
  return open.reduce((best, m) => ((effDay(m) as number) < (effDay(best) as number) ? m : best));
}

/**
 * THE SLIP SERIES for a milestone's sparkline — the forecast's drift past the
 * plan over the recorded days. Each point is days-late as of that snapshot; a
 * flat line is on plan, a rising line is a milestone running away. Empty until
 * the daily job has recorded a day or two (the drill then reads "building").
 */
export function slipSeries(
  pulses: MilestonePulse[],
  plannedDay: string | undefined,
): { day: string; slipDays: number }[] {
  const p = dayNum(plannedDay);
  return [...pulses]
    .sort((a, b) => (dayNum(a.pulseOn) ?? 0) - (dayNum(b.pulseOn) ?? 0))
    .map((row) => {
      // Prefer the CADENCE prediction the day carried (0215) so the line shows the
      // forecast drifting from the board's own pace; fall back to the plan forecast
      // on a day with no prediction (an untied point, or before enough history).
      const f = dayNum(row.predictedDay) ?? dayNum(row.forecastDay);
      const slip = p != null && f != null ? Math.max(0, f - p) : 0;
      return { day: row.pulseOn, slipDays: slip };
    });
}
